import { useMemo, useState } from "react";
import {
  Paper,
  Stack,
  Group,
  SegmentedControl,
  Button,
  Textarea,
  Text,
  Title,
  Badge,
  ScrollArea,
  Alert,
  Code,
  Divider,
  Tooltip,
  ActionIcon,
} from "@mantine/core";
import {
  IconSend,
  IconRefresh,
  IconCopy,
  IconCheck,
  IconAlertTriangle,
  IconBriefcase,
} from "@tabler/icons-react";
import { useMutation } from "@tanstack/react-query";
import { notifications } from "@mantine/notifications";
import { Link } from "react-router-dom";
import { Anchor } from "@mantine/core";
import { grInvoiceAutomationApi } from "../../../../api/grInvoiceAutomation.api";
import { apiError } from "../../../../api/client";
import { useAuth } from "../../../../auth/AuthContext";
import { SAMPLE_FULL, SAMPLE_MISMATCHED, SAMPLE_NOT_INVOICE } from "./samples";

const KIND_LABELS = {
  full: "Full (matched)",
  mismatched: "Mismatched",
  not_invoice: "Not invoice",
};

function sampleForKind(kind) {
  if (kind === "full") return SAMPLE_FULL;
  if (kind === "not_invoice") return SAMPLE_NOT_INVOICE;
  return SAMPLE_MISMATCHED;
}

/**
 * Postman-style tester for the inbound AP Invoice webhooks. Calls the
 * session-authenticated `/inbound/test/{kind}` siblings so the user doesn't
 * need an API key to verify behaviour end-to-end.
 */
export function TesterPanel({ onInvoiceStored }) {
  const { user } = useAuth();
  const hasWorkbench =
    user?.isAdmin || !!user?.modulePermissions?.["ap-workbench"];

  const [kind, setKind] = useState("full");
  const [body, setBody] = useState(() => JSON.stringify(SAMPLE_FULL, null, 2));
  const [parseError, setParseError] = useState(null);
  const [lastResponse, setLastResponse] = useState(null);
  const [copied, setCopied] = useState(false);

  const sendMut = useMutation({
    mutationFn: async () => {
      let parsed;
      try {
        parsed = JSON.parse(body);
      } catch (e) {
        const err = new Error(`Invalid JSON: ${e.message}`);
        err.local = true;
        throw err;
      }
      const endpointKind = kind === "not_invoice" ? "mismatched" : kind;
      return grInvoiceAutomationApi.testInbound(endpointKind, parsed);
    },
    onSuccess: (data) => {
      setLastResponse({ ok: true, data });
      notifications.show({
        color: "green",
        message: data.replaced
          ? "Invoice overwritten (existing draft)"
          : "Invoice stored",
      });
      onInvoiceStored?.(data.invoice);
    },
    onError: (err) => {
      if (err?.local) {
        setParseError(err.message);
        notifications.show({ color: "red", message: err.message });
        return;
      }
      const payload = err?.response?.data || { error: { message: apiError(err) } };
      setLastResponse({
        ok: false,
        status: err?.response?.status,
        data: payload,
      });
      notifications.show({ color: "red", message: apiError(err) });
    },
  });

  const switchKind = (next) => {
    setKind(next);
    setBody(JSON.stringify(sampleForKind(next), null, 2));
    setParseError(null);
    setLastResponse(null);
  };

  const resetSample = () => {
    setBody(JSON.stringify(sampleForKind(kind), null, 2));
    setParseError(null);
  };

  const formatBody = () => {
    try {
      setBody(JSON.stringify(JSON.parse(body), null, 2));
      setParseError(null);
    } catch (e) {
      setParseError(`Invalid JSON: ${e.message}`);
    }
  };

  const responseText = useMemo(() => {
    if (!lastResponse) return "";
    return JSON.stringify(lastResponse.data, null, 2);
  }, [lastResponse]);

  const copyResponse = async () => {
    if (!responseText) return;
    try {
      await navigator.clipboard.writeText(responseText);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      notifications.show({ color: "red", message: "Clipboard unavailable" });
    }
  };

  const endpointKind = kind === "not_invoice" ? "mismatched" : kind;
  const endpointPath = `/api/modules/gr-invoice-automation/inbound/${endpointKind}`;

  return (
    <Stack gap="md">
      <Paper p="md">
        <Stack gap="sm">
          <Group justify="space-between" wrap="wrap">
            <div>
              <Title order={4}>Inbound webhook tester</Title>
              <Text size="sm" c="dimmed">
                Sends to the same handler Duvo will call — the session-auth
                twin so no API key is needed in-app.
              </Text>
            </div>
            <SegmentedControl
              value={kind}
              onChange={switchKind}
              data={[
                { label: KIND_LABELS.full, value: "full" },
                { label: KIND_LABELS.mismatched, value: "mismatched" },
                { label: KIND_LABELS.not_invoice, value: "not_invoice" },
              ]}
            />
          </Group>
          <Group gap="xs">
            <Text size="xs" c="dimmed">
              POST
            </Text>
            <Code>{endpointPath}</Code>
            <Badge variant="light" color="blue" size="sm">
              External callers: X-API-Key header
            </Badge>
          </Group>
        </Stack>
      </Paper>

      <Paper p="md">
        <Stack gap="xs">
          <Group justify="space-between" wrap="wrap">
            <Group gap="xs">
              <Text fw={600} size="sm">
                Request body
              </Text>
              <Text size="xs" c="dimmed">
                {body.split("\n").length} lines •{" "}
                {body.length.toLocaleString()} chars
              </Text>
            </Group>
            <Group gap="xs">
              <Tooltip label="Re-format JSON">
                <Button
                  variant="subtle"
                  size="xs"
                  onClick={formatBody}
                  leftSection={<IconRefresh size={14} />}
                >
                  Format
                </Button>
              </Tooltip>
              <Button
                variant="subtle"
                size="xs"
                onClick={resetSample}
                leftSection={<IconRefresh size={14} />}
              >
                Reset to sample
              </Button>
              <Button
                leftSection={<IconSend size={16} />}
                loading={sendMut.isPending}
                onClick={() => {
                  setParseError(null);
                  sendMut.mutate();
                }}
              >
                Send
              </Button>
            </Group>
          </Group>
          <Textarea
            value={body}
            onChange={(e) => {
              setBody(e.currentTarget.value);
              setParseError(null);
            }}
            autosize
            minRows={14}
            maxRows={30}
            styles={{
              input: {
                fontFamily:
                  "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                fontSize: 12,
                lineHeight: 1.5,
              },
            }}
            error={parseError}
            spellCheck={false}
          />
        </Stack>
      </Paper>

      {lastResponse && (
        <Paper p="md">
          <Stack gap="xs">
            <Group justify="space-between">
              <Group gap="xs">
                <Text fw={600} size="sm">
                  Response
                </Text>
                {lastResponse.ok ? (
                  <Badge color="green" variant="light">
                    {lastResponse.data?.replaced ? "200 Replaced" : "201 Created"}
                  </Badge>
                ) : (
                  <Badge color="red" variant="light">
                    {lastResponse.status || "Error"}
                  </Badge>
                )}
                {lastResponse.ok && lastResponse.data?.invoice && (
                  <>
                    <Badge
                      color={
                        lastResponse.data.invoice.denormStatus === "ok"
                          ? "teal"
                          : "yellow"
                      }
                      variant="light"
                    >
                      denorm: {lastResponse.data.invoice.denormStatus}
                    </Badge>
                    <Badge
                      color={
                        lastResponse.data.invoice.status === "matched"
                          ? "green"
                          : "orange"
                      }
                      variant="light"
                    >
                      {lastResponse.data.invoice.status}
                    </Badge>
                  </>
                )}
              </Group>
              <Tooltip label={copied ? "Copied" : "Copy response JSON"}>
                <ActionIcon variant="subtle" onClick={copyResponse}>
                  {copied ? (
                    <IconCheck size={16} />
                  ) : (
                    <IconCopy size={16} />
                  )}
                </ActionIcon>
              </Tooltip>
            </Group>
            {lastResponse.ok &&
              hasWorkbench &&
              lastResponse.data?.invoice?.id && (
                <Anchor
                  component={Link}
                  to={`/modules/ap-workbench?invoiceId=${lastResponse.data.invoice.id}`}
                  size="sm"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                >
                  <IconBriefcase size={14} /> Open this invoice in AP Workbench
                </Anchor>
              )}
            {!lastResponse.ok && lastResponse.data?.error?.code === "INVOICE_ALREADY_PUSHED" && (
              <Alert color="red" icon={<IconAlertTriangle size={16} />}>
                This invoice is already pushed to SAP and cannot be overwritten
                via the webhook. (AP Workbench handles corrections.)
              </Alert>
            )}
            <Divider />
            <ScrollArea h={280}>
              <Code block style={{ whiteSpace: "pre", fontSize: 12 }}>
                {responseText}
              </Code>
            </ScrollArea>
          </Stack>
        </Paper>
      )}
    </Stack>
  );
}
